import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { LANDING } from '@/constants/testIds';

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const APP_STORE_URL =
        'https://apps.apple.com/in/app/hypertroop-fit/id6771863680';

    const links = [
        { id: LANDING.navLinkFeatures, label: 'Features', href: '#features' },
        { id: LANDING.navLinkHowItWorks, label: 'How it works', href: '#how' },
        { id: LANDING.navLinkTestimonials, label: 'Testimonials', href: '#testimonials' },
    ];

    return (
        <nav
            data-testid={LANDING.nav}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? 'nav-blur py-3' : 'py-5 bg-transparent'
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
                {/* Logo */}
                <a
                    href="#top"
                    data-testid={LANDING.navLogo}
                    className="flex items-center gap-2 group"
                >
                    <div className="w-7 h-7 rounded-lg bg-[#b8ff00] flex items-center justify-center transition-transform group-hover:rotate-12">
                        <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4 text-black"
                            fill="currentColor"
                        >
                            <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
                        </svg>
                    </div>
                    <span className="text-white font-bold tracking-tight text-base">
                        HyperTroop<span className="text-[#b8ff00]">.Fit</span>
                    </span>
                </a>

                {/* Desktop links */}
                <div className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={l.id}
                            className="text-sm text-[#8e8e93] hover:text-white transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                {/* Download CTA */}
                <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={LANDING.navDownloadBtn}
                    className="hidden md:inline-flex btn-primary-lime items-center gap-2 px-5 py-2.5 rounded-full text-sm"
                >
                    Download
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                </a>

                {/* Mobile toggle */}
                <button
                    data-testid={LANDING.navMobileToggle}
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden w-10 h-10 rounded-full border border-white/10 text-white flex items-center justify-center"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="md:hidden mt-3 mx-5 surface-card p-5 space-y-4 animate-fade-in">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={`mobile-${l.id}`}
                            onClick={() => setOpen(false)}
                            className="block text-base text-white/80 hover:text-[#b8ff00]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`mobile-${LANDING.navDownloadBtn}`}
                        className="btn-primary-lime inline-flex w-full justify-center items-center px-5 py-3 rounded-full text-sm"
                    >
                        Download
                    </a>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
